import * as XLSX from "xlsx";

/**
 * Parse an uploaded spreadsheet File into a tab-aware structure the mapper can
 * render: every sheet, each as a 2D array of rows. This reads the REAL bytes the
 * user dropped (xlsx/xls/csv) — no canned previews.
 */
export const parseWorkbook = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const workbook = XLSX.read(event.target.result, { type: "array" });
        const sheets = workbook.SheetNames.map((name) => ({
          name,
          rows: XLSX.utils.sheet_to_json(workbook.Sheets[name], {
            header: 1,
            blankrows: false,
            defval: ""
          })
        }));
        resolve({ fileName: file.name, sheets });
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });

const countNonEmpty = (row = []) =>
  row.filter((cell) => String(cell).trim() !== "").length;

/**
 * Heuristic: the header row is usually the early row with the most filled cells.
 * Returns the index of that row within the first 12 rows.
 */
export const guessHeaderRowIndex = (rows) => {
  const scanWindow = rows.slice(0, 12);
  if (!scanWindow.length) {
    return 0;
  }
  const best = scanWindow.reduce(
    (winner, row, index) => {
      const count = countNonEmpty(row);
      return count > winner.count ? { index, count } : winner;
    },
    { index: 0, count: -1 }
  );
  return best.index;
};

/**
 * Sheets a parser should usually skip — reference/instruction tabs that carry no
 * BOM line items (the PCNA "Project Evolucio" workbooks are full of these).
 */
const noiseSheetPattern = /instruction|example|dropdown|battery|material table|notes|cover|legend/i;

export const guessRelevantSheetName = (sheets) => {
  const named = sheets.find(({ name }) => /workfile|bom|data/i.test(name));
  if (named) {
    return named.name;
  }
  const withSignal = sheets
    .filter(({ name }) => !noiseSheetPattern.test(name))
    .map((sheet) => ({
      name: sheet.name,
      cells: sheet.rows.reduce((sum, row) => sum + countNonEmpty(row), 0)
    }))
    .sort((a, b) => b.cells - a.cells);
  return withSignal[0]?.name || sheets[0]?.name || null;
};

export const isLikelyNoiseSheet = (name) => noiseSheetPattern.test(name);

/**
 * Turn a header row into column descriptors. Blank headers fall back to a
 * spreadsheet-style letter so every column is still selectable.
 */
export const columnsFromHeaderRow = (headerRow = []) =>
  headerRow.map((label, index) => {
    const text = String(label).trim();
    return {
      index,
      label: text || `Column ${XLSX.utils.encode_col(index)}`
    };
  });
